import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Image as ImageIcon, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import CustomerCombobox from "@/components/CustomerCombobox";

const STATUSES = ["Lead", "RFQ", "Feasibility", "Quotation Submitted", "Sample Submission", "Technical/Commercial Approval", "PO", "Loss/Regret"];
const SEGMENTS = ["Auto", "Non-Auto", "Domestic", "Export"];

/**
 * Edit dialog that also lets the user manage the part's BOM:
 *  - For an ASSEMBLY (or sub-assembly): list of direct children with qty
 *    and add new children inline. Existing child rows show the part name +
 *    a "+sub" button to navigate to that child's edit (where its own
 *    children are managed) — keeping the dialog UI shallow.
 *  - For a CHILD: shows the parent assemblies (where-used) at the top
 *    AND lets the user attach further children to itself (it becomes a
 *    sub-assembly automatically on save).
 */
export default function PartEditDialog({ open, onOpenChange, part, onSaved }) {
    const fileRef = useRef(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const [form, setForm] = useState(() => fromPart(part));
    const [allParts, setAllParts] = useState([]);
    const [parents, setParents] = useState([]);
    const [bomTree, setBomTree] = useState([]);

    useEffect(() => { setForm(fromPart(part)); }, [part]);

    useEffect(() => {
        if (!open) return;
        let cancel = false;
        api.get(`/parts`).then((r) => { if (!cancel) setAllParts(r.data || []); }).catch(() => { });
        if (part?.part_id) {
            api.get(`/parts/${part.part_id}/where-used`).then((r) => { if (!cancel) setParents(r.data || []); }).catch(() => { });
            api.get(`/parts/${part.part_id}/bom-tree`).then((r) => {
                if (cancel) return;
                const tree = r.data || [];
                setBomTree(tree);
                // Convert flat DFS list (outline 1, 1.1, 1.1.1 …) into a parent_idx-linked
                // array so the existing recursive renderer can show + edit ALL descendants.
                const outlineToIdx = {};
                const rows = tree.map((node, i) => {
                    outlineToIdx[node.outline] = i;
                    const segs = (node.outline || "").split(".");
                    const parentOutline = segs.length > 1 ? segs.slice(0, -1).join(".") : null;
                    return {
                        part_id: node.part_id,
                        quantity: node.qty,
                        parent_idx: parentOutline != null ? (outlineToIdx[parentOutline] ?? null) : null,
                        _display_name: node.part_name,
                        _display_number: node.part_number,
                        _photo_path: node.photo_path,
                        _is_assembly: node.is_assembly,
                    };
                });
                setForm((f) => ({ ...f, child_parts: rows }));
            }).catch(() => { });
        } else {
            setParents([]);
            setBomTree([]);
        }
        return () => { cancel = true; };
    }, [open, part?.part_id]);

    const uploadPhoto = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        setPhotoUploading(true);
        try {
            const res = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setForm((f) => ({ ...f, photo_path: res.data.path }));
        } catch { toast.error("Photo upload failed"); }
        finally { setPhotoUploading(false); }
    };

    const save = async () => {
        if (!form.part_name.trim()) { toast.error("Part name required"); return; }
        try {
            // Step 1: persist any newly-added rows as new parts; build a row → part_id map.
            const rowToPartId = {};
            for (let i = 0; i < (form.child_parts || []).length; i++) {
                const c = form.child_parts[i];
                if (c.part_id) {
                    rowToPartId[i] = c.part_id;
                } else if (c._new_name && c._new_name.trim()) {
                    const cr = await api.post(`/parts`, {
                        part_name: c._new_name.trim(),
                        part_number: c._new_number || "",
                        is_child: true,
                        project_years: 5,
                        status: "Lead",
                        segment: "Auto",
                    });
                    rowToPartId[i] = cr.data.part_id;
                }
            }
            const childrenOf = (parentIdx) =>
                (form.child_parts || [])
                    .map((c, i) => ({ c, i }))
                    .filter((x) => (x.c.parent_idx ?? null) === parentIdx)
                    .map((x) => ({ part_id: rowToPartId[x.i], quantity: Number(x.c.quantity) || 1 }))
                    .filter((x) => x.part_id);

            // Step 2: persist THIS part with its direct children (parent_idx == null)
            const directChildren = childrenOf(null);
            const wasAssembly = !!part?.is_assembly;
            const becomesAssembly = (form._bom_role === "assembly" || form._bom_role === "sub_assembly") || wasAssembly || directChildren.length > 0;
            const isChild = (form._bom_role === "child" || form._bom_role === "sub_assembly");
            await api.put(`/parts/${part.part_id}`, {
                ...form,
                annual_volume: Number(form.annual_volume) || 0,
                target_price: Number(form.target_price) || 0,
                project_years: Number(form.project_years) || 5,
                po_volume: Number(form.po_volume) || 0,
                is_assembly: becomesAssembly,
                is_child: isChild,
                child_parts: directChildren,
            });

            // Step 3: PATCH each row that has its own sub-children (depth ≥ 2)
            const parentsWithKids = new Set(
                (form.child_parts || [])
                    .map((c) => c.parent_idx)
                    .filter((p) => p != null && p !== undefined)
            );
            for (const parentIdx of parentsWithKids) {
                const containerPartId = rowToPartId[parentIdx];
                if (!containerPartId) continue;
                const subChildren = childrenOf(parentIdx);
                await api.patch(`/parts/${containerPartId}/child-parts`, { child_parts: subChildren });
            }

            const totalDesc = (form.child_parts || []).length;
            toast.success(`Part updated · ${totalDesc} descendant${totalDesc !== 1 ? "s" : ""}`);
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            const status = e?.response?.status || 0;
            const detail = e?.response?.data?.detail;
            const isTransient = !status || [502, 503, 504, 408, 520, 521, 522, 523, 524].includes(status);
            const msg = typeof detail === "string"
                ? detail
                : Array.isArray(detail)
                    ? detail.map((d) => `${d.loc?.join?.(".") || ""}: ${d.msg}`).join(" · ")
                    : status === 401 ? "Session expired — please sign in again."
                    : (isTransient ? "Network is jittery — please click Save again in a moment." : (e?.message || "Could not save changes"));
            console.error("[PartEditDialog.save] error:", status, e?.response?.data || e);
            if (isTransient) toast.warning(`Save delayed · ${msg}`);
            else toast.error(`Save failed: ${msg}`);
        }
    };

    const partsById = Object.fromEntries(allParts.map((p) => [p.part_id, p]));
    const eligibleChildren = allParts.filter((p) => p.part_id !== part?.part_id && p.is_child);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-none max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
                <DialogHeader>
                    <DialogTitle>
                        Edit Part
                        {part?.is_assembly && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#002FA7] text-white px-2 py-0.5">Assembly</span>}
                        {part?.is_child && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FFB703] text-zinc-800 px-2 py-0.5">Child</span>}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-zinc-500">
                        Edit metadata, link parents, build the BOM tree (1, 1.1, 1.1.1 …) and attach PLM drawings.
                    </DialogDescription>
                </DialogHeader>
                <div className="overflow-y-auto flex-1 -mr-2 pr-2" data-testid="edit-part-body">
                    {/* Where-used (only if this part is referenced anywhere) */}
                    {parents.length > 0 && (
                        <div className="border-l-4 border-[#FFB703] bg-[#FFFBEB] px-3 py-2 mb-3 text-xs" data-testid="edit-parents-banner">
                            <div className="font-semibold text-zinc-700 uppercase tracking-wider text-[10px] mb-1">Used in {parents.length} assembl{parents.length > 1 ? "ies" : "y"}</div>
                            <div className="flex flex-wrap gap-2">
                                {parents.map((p) => (
                                    <span key={p.part_id} className="px-2 py-0.5 bg-white border border-zinc-200 font-mono text-[11px]">
                                        {p.part_name}{p.part_number ? ` (${p.part_number})` : ""} <span className="text-zinc-400">× {p.qty_in_parent}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Part Name *">
                            <Input value={form.part_name} onChange={(e) => setForm({ ...form, part_name: e.target.value })} data-testid="edit-part-name" />
                        </Field>
                        <Field label="Part Number">
                            <Input value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} />
                        </Field>
                        <Field label="Customer Name">
                            <CustomerCombobox value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="edit-customer" />
                        </Field>
                        <Field label="Segment">
                            <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
                                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                                <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="Status">
                            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                        </Field>
                        <Field label="BOM Role">
                            <Select value={form._bom_role} onValueChange={(v) => setForm({ ...form, _bom_role: v })}>
                                <SelectTrigger className="rounded-none" data-testid="edit-bom-role"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="assembly">Finished Good (Assembly)</SelectItem>
                                    <SelectItem value="child">Child Part (Sub-component)</SelectItem>
                                    <SelectItem value="sub_assembly">Sub-assembly (Child + has children)</SelectItem>
                                    <SelectItem value="standalone">Standalone / Legacy</SelectItem>
                                </SelectContent>
                            </Select>
                        </Field>
                        <Field label="Project Years (for ROI)">
                            <Input type="number" min="1" max="20" value={form.project_years} onChange={(e) => setForm({ ...form, project_years: e.target.value })} />
                        </Field>
                        {form.status === "PO" && (
                            <Field label="PO Volume (confirmed)">
                                <Input type="number" min="0" value={form.po_volume ?? 0} onChange={(e) => setForm({ ...form, po_volume: e.target.value })} data-testid="edit-po-volume" />
                            </Field>
                        )}
                        <Field label="Annual Volume">
                            <Input type="number" value={form.annual_volume} onChange={(e) => setForm({ ...form, annual_volume: e.target.value })} />
                        </Field>
                        <Field label="Target Price (Rs/piece)">
                            <Input type="number" value={form.target_price} onChange={(e) => setForm({ ...form, target_price: e.target.value })} />
                        </Field>
                        <div className="col-span-2">
                            <Field label="Photo">
                                <div className="flex items-center gap-3">
                                    {form.photo_path ? (
                                        <img src={`${API}/files/${form.photo_path}`} alt="part" className="w-16 h-16 object-cover border border-zinc-200" />
                                    ) : (
                                        <div className="w-16 h-16 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-400">
                                            <ImageIcon size={20} />
                                        </div>
                                    )}
                                    <input ref={fileRef} type="file" accept="image/*" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} className="hidden" />
                                    <Button type="button" variant="outline" className="rounded-none" onClick={() => fileRef.current?.click()} disabled={photoUploading}>
                                        <Upload size={14} className="mr-2" /> {photoUploading ? "Uploading…" : "Change photo"}
                                    </Button>
                                </div>
                            </Field>
                        </div>
                        <div className="col-span-2">
                            <Field label="Notes">
                                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                            </Field>
                        </div>
                    </div>

                    {/* Children (BOM) management — fully editable multi-level tree with `+sub`,
                        delete, photo-preview, and qty edit at EVERY level (1, 1.1, 1.1.1 …).
                        Saves recursively: this part's direct children via PUT, then each
                        deeper container via PATCH /parts/{id}/child-parts. */}
                    <div className="mt-5 border-t border-zinc-200 pt-3" data-testid="edit-children-section">
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono">
                                BOM tree — edit every level (1, 1.1, 1.1.1 …)
                                <span className="ml-2 text-zinc-400 normal-case">{(form.child_parts || []).length} row{(form.child_parts || []).length !== 1 ? "s" : ""}</span>
                            </Label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, child_parts: [...(form.child_parts || []), { _new_name: "", _new_number: "", quantity: 1, parent_idx: null }] })}
                                    className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline font-mono"
                                    data-testid="add-edit-child-new"
                                ><Plus size={12} className="inline" /> New level-1 child</button>
                                <button
                                    type="button"
                                    onClick={() => setForm({ ...form, child_parts: [...(form.child_parts || []), { part_id: "", quantity: 1, parent_idx: null }] })}
                                    className="text-[11px] uppercase tracking-wider text-[#002FA7] hover:underline font-mono"
                                    data-testid="add-edit-child-existing"
                                ><Plus size={12} className="inline" /> Link existing</button>
                            </div>
                        </div>
                        {(form.child_parts || []).length === 0 ? (
                            <div className="text-xs text-zinc-400 italic border border-dashed border-zinc-200 py-3 text-center">
                                No child parts. Click &ldquo;New level-1 child&rdquo; to create one or &ldquo;Link existing&rdquo; to attach an existing child.
                            </div>
                        ) : (() => {
                            // Outline numbering using parent_idx graph
                            const rows = form.child_parts;
                            const childrenOf = (parentIdx) => rows.map((r, i) => ({ r, i })).filter((x) => (x.r.parent_idx ?? null) === parentIdx);
                            const numberMap = {};
                            const walk = (parentIdx, prefix) => {
                                childrenOf(parentIdx).forEach((node, ord) => {
                                    const num = prefix ? `${prefix}.${ord + 1}` : `${ord + 1}`;
                                    numberMap[node.i] = num;
                                    walk(node.i, num);
                                });
                            };
                            walk(null, "");
                            const out = [];
                            const render = (parentIdx, depth) => {
                                childrenOf(parentIdx).forEach((node) => {
                                    const i = node.i;
                                    const c = node.r;
                                    const number = numberMap[i] || "?";
                                    const linked = c.part_id ? partsById[c.part_id] : null;
                                    // Photo: prefer current snapshot from allParts, fall back to
                                    // the BOM tree's photo_path that we loaded on open.
                                    const photoPath = linked?.photo_path || c._photo_path || "";
                                    out.push(
                                        <div key={i} className="flex items-center gap-2 mb-2 text-sm" data-testid={`edit-child-row-${i}`} style={{ paddingLeft: depth * 20 }}>
                                            <span className="text-[10px] font-mono font-semibold text-[#002FA7] w-12 shrink-0" data-testid={`edit-child-outline-${i}`}>{number}</span>
                                            {photoPath ? (
                                                <img src={`${API}/files/${photoPath}`} alt="" className="w-9 h-9 object-cover border border-zinc-200 shrink-0" data-testid={`edit-child-photo-${i}`} />
                                            ) : (
                                                <div className="w-9 h-9 border border-dashed border-zinc-300 flex items-center justify-center text-zinc-300 shrink-0" title="No photo (open ↗ to add one)">
                                                    <ImageIcon size={14} />
                                                </div>
                                            )}
                                            {c.part_id ? (
                                                <Select
                                                    value={c.part_id || ""}
                                                    onValueChange={(v) => { const next = [...form.child_parts]; next[i] = { ...next[i], part_id: v }; setForm({ ...form, child_parts: next }); }}
                                                >
                                                    <SelectTrigger className="rounded-none h-8 flex-1"><SelectValue placeholder="Pick a child part" /></SelectTrigger>
                                                    <SelectContent>
                                                        {eligibleChildren.map((p) => (
                                                            <SelectItem key={p.part_id} value={p.part_id}>{p.part_name} {p.part_number ? `(${p.part_number})` : ""}</SelectItem>
                                                        ))}
                                                        {eligibleChildren.length === 0 && <div className="px-2 py-1 text-xs text-zinc-400 italic">No existing child parts — use &ldquo;New child&rdquo; instead.</div>}
                                                    </SelectContent>
                                                </Select>
                                            ) : (
                                                <>
                                                    <Input placeholder="Child name" value={c._new_name || ""} onChange={(e) => { const next = [...form.child_parts]; next[i] = { ...next[i], _new_name: e.target.value }; setForm({ ...form, child_parts: next }); }} className="rounded-none h-8 flex-1" data-testid={`edit-child-name-${i}`} />
                                                    <Input placeholder="Part #" value={c._new_number || ""} onChange={(e) => { const next = [...form.child_parts]; next[i] = { ...next[i], _new_number: e.target.value }; setForm({ ...form, child_parts: next }); }} className="rounded-none h-8 w-28" />
                                                </>
                                            )}
                                            <Input type="number" min="0.01" step="1" value={c.quantity ?? 1} onChange={(e) => { const next = [...form.child_parts]; next[i] = { ...next[i], quantity: e.target.value }; setForm({ ...form, child_parts: next }); }} className="rounded-none h-8 w-20 font-mono" data-testid={`edit-child-qty-${i}`} />
                                            <button type="button" onClick={() => setForm({ ...form, child_parts: [...form.child_parts, { _new_name: "", _new_number: "", quantity: 1, parent_idx: i }] })} className="text-[#002FA7] hover:underline text-[10px] uppercase tracking-wider whitespace-nowrap" title="Add sub-child under this row" data-testid={`edit-child-add-sub-${i}`}>+sub</button>
                                            {linked && (
                                                <a href={`/parts/${linked.part_id}/costing`} target="_blank" rel="noreferrer" className="text-[10px] text-[#002FA7] hover:underline font-mono uppercase tracking-wider whitespace-nowrap" title="Open child's costing">
                                                    open ↗
                                                </a>
                                            )}
                                            <button type="button" onClick={() => {
                                                // Remove this row + all descendants
                                                const drop = new Set([i]);
                                                let changed = true;
                                                while (changed) {
                                                    changed = false;
                                                    form.child_parts.forEach((r, idx) => {
                                                        if (r.parent_idx != null && drop.has(r.parent_idx) && !drop.has(idx)) { drop.add(idx); changed = true; }
                                                    });
                                                }
                                                const remap = {};
                                                const next = [];
                                                form.child_parts.forEach((r, idx) => { if (!drop.has(idx)) { remap[idx] = next.length; next.push(r); } });
                                                const fixed = next.map((r) => ({ ...r, parent_idx: r.parent_idx == null ? null : (remap[r.parent_idx] ?? null) }));
                                                setForm({ ...form, child_parts: fixed });
                                            }} className="text-zinc-400 hover:text-[#E63946]" data-testid={`edit-child-remove-${i}`}><Trash2 size={14} /></button>
                                        </div>
                                    );
                                    render(i, depth + 1);
                                });
                            };
                            render(null, 0);
                            return out;
                        })()}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" className="rounded-none" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227B]" onClick={save} data-testid="save-edit-part">Save changes</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function fromPart(p) {
    const isAssembly = !!p?.is_assembly;
    const isChild = !!p?.is_child;
    let role = "standalone";
    if (isAssembly && isChild) role = "sub_assembly";
    else if (isAssembly) role = "assembly";
    else if (isChild) role = "child";
    return {
        _bom_role: role,
        part_name: p?.part_name || "",
        part_number: p?.part_number || "",
        customer_name: p?.customer_name || "",
        annual_volume: p?.annual_volume || 0,
        target_price: p?.target_price || 0,
        segment: p?.segment || "Auto",
        status: p?.status || "Lead",
        photo_path: p?.photo_path || "",
        project_years: p?.project_years || 5,
        yearly_volumes: p?.yearly_volumes || [],
        po_volume: p?.po_volume || 0,
        notes: p?.notes || "",
        child_parts: (p?.child_parts || []).map((c) => ({ part_id: c.part_id, quantity: c.quantity })),
    };
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</Label>
            {children}
        </div>
    );
}
